/**
 * useMentionComposer — @-mention logic for a contentEditable div.
 *
 * Detects "@" in text, manages popup state, keyboard navigation,
 * and inserts inline chips into the editable div.
 */

import {
  useCallback,
  useState,
  type KeyboardEvent,
  type RefObject,
} from "react";
import { useMentionSearch } from "./useMentionSearch";
import { normalizeSchemaId } from "../../../components/shared/entityHref";
import { comboItemCount } from "../../../components/shared/MentionPopup";
import type { EntityMention, EntitySearchResult } from "../types";

// Data attributes on chip elements
const CHIP_ATTR = "data-mention-id";
const CHIP_SCHEMA_ATTR = "data-mention-schema";

/**
 * Build a DocumentFragment for inserting plain (pasted/dictated) text into the
 * contentEditable. Line endings are normalized to `\n` (CRLF / bare CR collapse
 * to one), and each line becomes a text node separated by a single <br>. This
 * round-trips through getTextContent (BR → "\n") to EXACTLY the normalized text —
 * unlike inserting one raw text node with embedded `\r\n`, which the pre-wrap
 * editor rendered with doubled gaps and leaked `\r` into the sent message.
 */
export function buildPlainTextFragment(raw: string): DocumentFragment {
  const normalized = raw.replace(/\r\n?/g, "\n");
  const frag = document.createDocumentFragment();
  normalized.split("\n").forEach((line, i) => {
    if (i > 0) frag.appendChild(document.createElement("br"));
    if (line) frag.appendChild(document.createTextNode(line));
  });
  return frag;
}

/** Collapse the selection to the very end of `el`. Used after restoring the
 *  editor's innerHTML across a collapsed↔expanded swap so continued typing /
 *  macOS dictation appends at the end instead of the start (which reordered
 *  words). */
export function placeCaretAtEnd(el: HTMLElement): void {
  const sel = window.getSelection();
  if (!sel) return;
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
}

export interface UseMentionComposerResult {
  readonly mentions: readonly EntityMention[];
  readonly isMentionActive: boolean;
  readonly mentionQuery: string;
  readonly selectedIndex: number;
  readonly searchResults: readonly EntitySearchResult[];
  readonly isSearchLoading: boolean;
  readonly activeCategory: string | null;
  readonly handleKeyDown: (e: KeyboardEvent<HTMLDivElement>) => boolean;
  readonly handleInput: () => void;
  readonly handleSelect: (item: EntitySearchResult) => void;
  readonly handleCategorySelect: (schemaId: string) => void;
  readonly handleCategoryBack: () => void;
  readonly clearAll: () => void;
  /** Extract plain text + mentions from the contentEditable div */
  readonly extractContent: () => { text: string; mentions: readonly EntityMention[] };
}

/** Get plain text from the editor, replacing chip nodes with @Name */
function getTextContent(el: HTMLElement): string {
  let text = "";
  for (const node of el.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent ?? "";
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      if (element.hasAttribute(CHIP_ATTR)) {
        const name = element.getAttribute("data-mention-name") ?? "";
        text += `@${name}`;
      } else if (element.tagName === "BR") {
        text += "\n";
      } else {
        text += getTextContent(element);
      }
    }
  }
  return text;
}

/** Get text before cursor for mention trigger detection.
 *  Chips are replaced with a placeholder (no @) so they don't re-trigger the popup. */
function getTextBeforeCursorForTrigger(el: HTMLElement): string | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;

  const range = sel.getRangeAt(0);
  const preRange = document.createRange();
  preRange.selectNodeContents(el);
  preRange.setEnd(range.startContainer, range.startOffset);

  const fragment = preRange.cloneContents();
  const tempDiv = document.createElement("div");
  tempDiv.appendChild(fragment);
  return getTextForTrigger(tempDiv);
}

/** Like getTextContent but chips become "\uFFFC" (object replacement char) instead of @Name */
function getTextForTrigger(el: HTMLElement): string {
  let text = "";
  for (const node of el.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent ?? "";
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      if (element.hasAttribute(CHIP_ATTR)) {
        text += "\uFFFC"; // placeholder — no @ so it won't trigger popup
      } else if (element.tagName === "BR") {
        text += "\n";
      } else {
        text += getTextForTrigger(element);
      }
    }
  }
  return text;
}

/** Delete text from cursor backwards by `count` characters (text nodes only) */
function deleteBeforeCursor(count: number): void {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return;

  for (let i = 0; i < count; i++) {
    const range = sel.getRangeAt(0);
    if (range.startOffset > 0) {
      range.setStart(range.startContainer, range.startOffset - 1);
      range.deleteContents();
    } else {
      // At the start of a text node — try previous sibling
      const prev = range.startContainer.previousSibling;
      if (prev?.nodeType === Node.TEXT_NODE) {
        const textLen = prev.textContent?.length ?? 0;
        if (textLen > 0) {
          const r = document.createRange();
          r.setStart(prev, textLen - 1);
          r.setEnd(prev, textLen);
          r.deleteContents();
          sel.removeAllRanges();
          sel.addRange(r);
        }
      }
    }
  }
}

/** Build a mention chip element using safe DOM APIs (no innerHTML). */
export function mentionChipSchemaId(schemaId: string): string {
  return normalizeSchemaId(schemaId);
}

function buildChipElement(item: {
  id: string;
  schema_id: string;
  name: string;
  displayName: string;
}): HTMLSpanElement {
  const chip = document.createElement("span");
  chip.contentEditable = "false";
  chip.setAttribute(CHIP_ATTR, item.id);
  chip.setAttribute(CHIP_SCHEMA_ATTR, item.schema_id);
  chip.setAttribute("data-mention-name", item.name);
  chip.className = "mention-chip";

  const icon = document.createElement("span");
  icon.className = "mention-chip-icon";
  icon.setAttribute("data-schema", mentionChipSchemaId(item.schema_id));
  chip.appendChild(icon);

  chip.appendChild(document.createTextNode(item.displayName));
  return chip;
}

/** Insert a chip element at the current cursor position */
function insertChipAtCursor(
  editorEl: HTMLElement,
  chip: HTMLElement,
): void {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return;

  const range = sel.getRangeAt(0);
  range.collapse(false);

  // Insert chip + trailing nbsp (regular space gets collapsed by contentEditable)
  const spaceNode = document.createTextNode("\u00A0");
  range.insertNode(spaceNode);
  range.insertNode(chip);

  // Move cursor after the nbsp so next keystroke (including @) works
  const newRange = document.createRange();
  if (spaceNode.textContent) {
    newRange.setStart(spaceNode, spaceNode.textContent.length);
    newRange.collapse(true);
  } else {
    newRange.setStartAfter(chip);
    newRange.collapse(true);
  }
  sel.removeAllRanges();
  sel.addRange(newRange);

  editorEl.focus();
}

/** Truncate name for chip display: strip newlines, limit to 30 chars */
function chipDisplayName(name: string): string {
  const clean = name.replace(/[\n\r]+/g, " ").trim();
  return clean.length > 30 ? clean.slice(0, 30) + "…" : clean;
}

export function useMentionComposer(
  editorRef: RefObject<HTMLDivElement | null>,
): UseMentionComposerResult {
  const [mentions, setMentions] = useState<readonly EntityMention[]>([]);
  const [mentionActive, setMentionActive] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const { results: searchResults, isLoading: isSearchLoading } =
    useMentionSearch(mentionQuery, mentionActive, activeCategory ?? undefined);

  const hasQuery = mentionQuery.length > 0;
  const isComboMode = !hasQuery && !activeCategory;
  const totalItems = isComboMode
    ? comboItemCount(searchResults.length)
    : searchResults.length;

  const closeMention = useCallback(() => {
    setMentionActive(false);
    setMentionQuery("");
    setSelectedIndex(0);
    setActiveCategory(null);
  }, []);

  const handleSelect = useCallback(
    (item: EntitySearchResult) => {
      if (!item.name) return;
      const el = editorRef.current;
      if (!el) return;

      const mention: EntityMention = {
        id: item.id,
        name: item.name,
        schemaId: item.schema_id,
      };

      setMentions((prev) => {
        if (prev.some((m) => m.id === item.id)) return prev;
        return [...prev, mention];
      });

      // Delete the "@query" text before cursor
      const deleteCount = 1 + mentionQuery.length; // "@" + query
      deleteBeforeCursor(deleteCount);

      // Build chip element using safe DOM APIs (no innerHTML)
      const displayName = chipDisplayName(item.name);
      const chip = buildChipElement({
        id: item.id,
        schema_id: item.schema_id,
        name: item.name,
        displayName,
      });

      insertChipAtCursor(el, chip);
      closeMention();
    },
    [editorRef, mentionQuery, closeMention],
  );

  const handleCategorySelect = useCallback((schemaId: string) => {
    setActiveCategory(schemaId);
    setSelectedIndex(0);
  }, []);

  const handleCategoryBack = useCallback(() => {
    setActiveCategory(null);
    setSelectedIndex(0);
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>): boolean => {
      if (!mentionActive) return false;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((i) => (i + 1) % Math.max(totalItems, 1));
          return true;

        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((i) => (i - 1 + Math.max(totalItems, 1)) % Math.max(totalItems, 1));
          return true;

        case "Enter": {
          e.preventDefault();
          if (isComboMode) {
            const recentCount = Math.min(searchResults.length, 4);
            if (selectedIndex < recentCount) {
              const item = searchResults[selectedIndex];
              if (item) handleSelect(item);
            } else {
              const catIndex = selectedIndex - recentCount;
              const categories = [
                "contacts.person", "email.message", "projects.task",
                "meetings.calendar_event", "telegram.message", "companies.company",
                "episodes.episode",
              ];
              const cat = categories[catIndex];
              if (cat) handleCategorySelect(cat);
            }
          } else {
            const item = searchResults[selectedIndex];
            if (item) handleSelect(item);
          }
          return true;
        }

        case "Escape":
          e.preventDefault();
          if (activeCategory) {
            handleCategoryBack();
          } else {
            closeMention();
          }
          return true;

        case "Tab":
          e.preventDefault();
          closeMention();
          return true;

        default:
          return false;
      }
    },
    [
      mentionActive, totalItems, isComboMode, searchResults,
      selectedIndex, activeCategory, handleSelect,
      handleCategorySelect, handleCategoryBack, closeMention,
    ],
  );

  /** Called on every input event from the contentEditable div */
  const handleInput = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;

    const textBefore = getTextBeforeCursorForTrigger(el);
    if (textBefore === null) return;

    // Find last "@" preceded by whitespace or at start (chips are \uFFFC, not @)
    const lastAtIndex = textBefore.lastIndexOf("@");
    if (lastAtIndex >= 0) {
      const charBefore = lastAtIndex > 0 ? textBefore[lastAtIndex - 1] : " ";
      if (charBefore === " " || charBefore === "\n" || charBefore === "\u00A0" || lastAtIndex === 0) {
        const query = textBefore.slice(lastAtIndex + 1);
        if (!mentionActive && !query.includes(" ")) {
          setMentionActive(true);
          setMentionQuery(query);
          setSelectedIndex(0);
          setActiveCategory(null);
        } else if (mentionActive) {
          setMentionQuery(query);
          setSelectedIndex(0);
        }
      } else if (mentionActive) {
        closeMention();
      }
    } else if (mentionActive) {
      closeMention();
    }

    // Sync mentions state with actual chips in DOM
    const chipEls = el.querySelectorAll(`[${CHIP_ATTR}]`);
    const chipIds = new Set<string>();
    chipEls.forEach((chip) => {
      const id = chip.getAttribute(CHIP_ATTR);
      if (id) chipIds.add(id);
    });
    setMentions((prev) => {
      const filtered = prev.filter((m) => chipIds.has(m.id));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [editorRef, mentionActive, closeMention]);

  const extractContent = useCallback((): {
    text: string;
    mentions: readonly EntityMention[];
  } => {
    const el = editorRef.current;
    if (!el) return { text: "", mentions: [] };

    const text = getTextContent(el);

    // Collect mentions from DOM chips
    const chipEls = el.querySelectorAll(`[${CHIP_ATTR}]`);
    const extractedMentions: EntityMention[] = [];
    chipEls.forEach((chip) => {
      const id = chip.getAttribute(CHIP_ATTR);
      const name = chip.getAttribute("data-mention-name");
      const schemaId = chip.getAttribute(CHIP_SCHEMA_ATTR);
      if (id && name && schemaId) {
        extractedMentions.push({ id, name, schemaId });
      }
    });

    return { text, mentions: extractedMentions };
  }, [editorRef]);

  const clearAll = useCallback(() => {
    setMentions([]);
    closeMention();
    const el = editorRef.current;
    if (el) el.innerHTML = "";
  }, [editorRef, closeMention]);

  return {
    mentions,
    isMentionActive: mentionActive,
    mentionQuery,
    selectedIndex,
    searchResults,
    isSearchLoading,
    activeCategory,
    handleKeyDown,
    handleInput,
    handleSelect,
    handleCategorySelect,
    handleCategoryBack,
    clearAll,
    extractContent,
  };
}
